import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import { FaMapMarkerAlt } from "react-icons/fa";
import dayjs from "dayjs";
import { useHomeContext } from "../../../context/Home_context";
import { buildYouTubeBackgroundEmbedUrl } from "../../../utils/helper";
import "../../home/homehero/HomeHero.css";
import { formatExpoTitle } from "../../home/homehero/HomeHero";

const ShowLogo = ({ show }) => {
  const imgSrc = show.image_full_path || "";
  const name = show.name || "";
  const link = show.link || null;

  if (!imgSrc) return null;

  const content = <img src={imgSrc} alt={name} loading="lazy" />;

  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="hh-show-card"
      >
        {content}
      </a>
    );
  }

  return <div className="hh-show-card">{content}</div>;
};

const EditionHero = () => {
  const { edition_expo_data } = useHomeContext();

  // Build embeddable YouTube background URL from API
  const youtubeEmbedUrl = buildYouTubeBackgroundEmbedUrl(
    edition_expo_data?.unlock_sucess_link,
  );

  // Format dates dynamically using dayjs
  const expoDates = useMemo(() => {
    const from =
      edition_expo_data?.from_date && dayjs(edition_expo_data.from_date);
    const to = edition_expo_data?.to_date && dayjs(edition_expo_data.to_date);

    if (!from?.isValid() || !to?.isValid()) return null;

    return {
      fromDay: from.format("DD"),
      toDay: to.format("DD"),
      isRange: !from.isSame(to, "day"),
      isSameMonth: from.isSame(to, "month"),
      fromMonth: from.format("MMMM").toUpperCase(),
      toMonth: to.format("MMMM").toUpperCase(),
      fromYear: from.format("YYYY"),
      toYear: to.format("YYYY"),
    };
  }, [edition_expo_data?.from_date, edition_expo_data?.to_date]);

  const welcomeText = useMemo(() => {
    const raw = edition_expo_data?.expo_title;
    if (!raw) return "";
    return String(raw)
      .replace(/<[^>]*>/g, "")
      .trim();
  }, [edition_expo_data?.expo_title]);

  const marqueeItems = useMemo(() => {
    const gallery = edition_expo_data?.gallerys;
    if (!gallery?.length) return [];

    const repeatCount = Math.ceil(12 / gallery.length);
    return Array.from({ length: repeatCount }, () => gallery).flat();
  }, [edition_expo_data?.gallerys]);

  return (
    <section className="hh-hero">
      {/* Video background */}
      <div className="hh-video-wrap" aria-hidden="true">
        {youtubeEmbedUrl ? (
          <iframe
            className="hh-video hh-video-yt"
            src={youtubeEmbedUrl}
            title="Bharat Buildcon Background Video"
            frameBorder="0"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : (
          <div className="hh-video-fallback" />
        )}
        <div className="hh-video-scrim" />
      </div>

      {/* Main content */}
      <div className="hh-content">
        {welcomeText && (
          <span className="hh-welcome-kicker">{welcomeText}</span>
        )}

        {(edition_expo_data?.logo_full_path || edition_expo_data?.logo) && (
          <div className="hh-hero-logos-wrapper">
            <Link
              to="/"
              className="hh-hero-logo"
              aria-label={edition_expo_data?.name || "Bharat Buildcon Edition"}
            >
              <img
                className="hh-hero-logo-img"
                src={
                  edition_expo_data.logo_full_path || edition_expo_data.logo
                }
                alt={edition_expo_data?.name || "Bharat Buildcon"}
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
            </Link>
          </div>
        )}

        {edition_expo_data?.expo_desc && (
          <div className="hh-slogan-box">
            <h2 className="hh-slogan-main">
              {formatExpoTitle(edition_expo_data.expo_desc)}
            </h2>
          </div>
        )}

        {(expoDates || edition_expo_data?.venue_name) && (
          <div className="hh-dates">
            <div className="hh-date-block">
              {expoDates && (
                <div className="hh-date-range">
                  <span className="hh-date-box notranslate" translate="no">
                    {expoDates.fromDay}
                  </span>

                  {expoDates.isRange && !expoDates.isSameMonth && (
                    <span className="hh-date-month">{expoDates.fromMonth}</span>
                  )}

                  {expoDates.isRange && (
                    <>
                      <span className="hh-date-dash">–</span>
                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.toDay}
                      </span>
                    </>
                  )}

                  <span className="hh-date-month">
                    {expoDates.isRange && !expoDates.isSameMonth
                      ? expoDates.toMonth
                      : expoDates.fromMonth}{" "}
                    <span className="notranslate" translate="no">
                      {expoDates.toYear}
                    </span>
                  </span>
                </div>
              )}
              {edition_expo_data?.venue_name &&
                (edition_expo_data?.venue_link ? (
                  <a
                    href={edition_expo_data.venue_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hh-date-venue"
                  >
                    <FaMapMarkerAlt className="hh-pin" />
                    {edition_expo_data.venue_name}
                  </a>
                ) : (
                  <span className="hh-date-venue">
                    <FaMapMarkerAlt className="hh-pin" />
                    {edition_expo_data.venue_name}
                  </span>
                ))}
            </div>
          </div>
        )}

        <div className="hh-cta-row hh-cta-row--center">
          <Link to="/exhibitors" className="hh-btn hh-btn--solid hh-btn--lg">
            Book your stall{" "}
          </Link>
          <Link to="/registration" className="hh-btn hh-btn--blue hh-btn--lg">
            Register as Visitor
          </Link>
        </div>
      </div>

      {marqueeItems.length > 0 && (
        <div className="hh-marquee-shell">
          <div className="hh-marquee-viewport">
            <Swiper
              modules={[Autoplay, FreeMode]}
              loop={true}
              speed={6000}
              autoplay={{
                delay: 0,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              freeMode={{
                enabled: true,
                momentum: false,
              }}
              slidesPerView="auto"
              spaceBetween={22}
              allowTouchMove={true}
              className="hh-swiper-marquee"
            >
              {marqueeItems.map((show, index) => (
                <SwiperSlide
                  key={`${show.id || show.name}-${index}`}
                  className="hh-swiper-slide"
                >
                  <ShowLogo show={show} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </section>
  );
};

export default EditionHero;


